import React, { useEffect, useState } from 'react';
import { useCart } from '../../../context/CartContext';
import { useWishlist } from '../../../context/WishlistContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TrendingProducts = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const wishlist = useWishlist();

  const API_BASE    = (import.meta).env.VITE_STOREFRONT_API_URL || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (import.meta).env.VITE_CUSTOMER_ID || '';

  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (!CUSTOMER_ID) return;
    const ctl = new AbortController();
    fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        const all = data?.products || [];
        // Featured first, then any others, capped at 4 — same shape the
        // template's render loop expects.
        const sorted = [...all].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        setProducts(sorted.slice(0, 4).map((p) => ({
          id: p.id,
          name: p.name,
          brand: 'Bounderlaster',
          price: (p.sale_price_cents ?? p.price_cents) / 100,
          rating: p.rating_avg ?? 0,
          reviews: p.review_count ?? 0,
          image: p.image_url,
          alt: p.name,
          badge: p.is_featured ? 'Bestseller' : (p.sale_price_cents != null ? 'Sale' : null),
        })));
      })
      .catch((err) => { if (err?.name !== 'AbortError') console.error('trending fetch failed:', err); });
    return () => ctl.abort();
  }, []);


  const handleProductClick = (product) => {
    navigate(`/product-details?id=${product.id}`);
  };

  const handleViewAll = () => {
    navigate('/product-catalog');
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-2">

              Trending Now
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-muted-foreground">

              Most loved products by beauty enthusiasts
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}>

            <Button
              onClick={handleViewAll}
              variant="outline"
              className="hidden md:flex items-center gap-2">

              View All
              <Icon name="ArrowRight" size={16} />
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products?.map((product, index) =>
          <motion.div
            key={product?.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -8 }}
            onClick={() => handleProductClick(product)}
            className="cursor-pointer group">

              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden aspect-square">
                  <img
                  src={product?.image}
                  alt={product?.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      {product?.badge}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); wishlist.toggle(product); }}
                    aria-label={wishlist.has(product?.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                  >
                    <Icon name="Heart" size={20} className={wishlist.has(product?.id) ? 'text-error fill-current' : 'text-foreground'} />
                  </button>
                </div>
                <div className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {product?.brand}
                  </p>
                  <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                    {product?.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-amber-500 fill-current" />
                      <span className="text-sm font-medium">{product?.rating}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ({product?.reviews})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-foreground">
                      ${product?.price}
                    </span>
                    <Button
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); cart.addItem(product, 1); }}
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 text-center md:hidden">

          <Button
            onClick={handleViewAll}
            variant="outline"
            className="w-full sm:w-auto">

            View All Products
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>);

};

export default TrendingProducts;